import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  AdsQueue, delay, getDeviceAds, IAdLoop, QueueConfig,
} from './requests';
import { cleanCurrentAd, parseConfig } from '../../utils/helpers';

import {
  axiosConfig, cdnURL, handleQueues, queues,
} from '../../config';

import { Actions, StrapiComponentType, useAdLoopReducer } from './useAdLoopReducer';
import AdPlayer from './AdPlayer';
import PortlTransition from './Transition';
import { AdType, Device } from '../../types';
import { useComponentRef } from './useComponentRef';

const AdContainer = styled.div`
    width: 100%;
    height: initial;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background-color: white;
  `;

const BrandedImage = styled.img`
    height: 101%;
    width: 101%;
  `;

type AdLoopProps = {
  device?: Device
};

export const DEFAULT_CONFIG = {
  queueCap: { ads: 5 },
  clientAdRequestIntervals: {
    ms: 2 * 1000,
  },
  weights: {
    server: 8,
    fallback: 1,
    component: 1,
    weather: 1,
    news: 1,
  },
} as Record<string, any>;

const AdLoop = ({ device }: AdLoopProps): React.ReactElement => {
  const [state, dispatch] = useAdLoopReducer();
  const { organization } = device || {};

  const componentRef = useComponentRef();

  const config = useRef<QueueConfig>(DEFAULT_CONFIG as QueueConfig);
  const controllerRef = useRef<AbortController>(new AbortController());

  const triggerPopFlow = async ({
    ad,
    onPopInit = () => {},
    onPopSuccess = () => {},
    onPopError = () => {},
    onPopFinally = () => {},
  }: {
    ad: IAdLoop;
    onPopInit?: () => void;
    onPopSuccess?: () => void;
    onPopError?: () => void;
    onPopFinally?: () => void;
  }) => {
    console.log(`[CLIENT] Triggering POP for ad ${ad.uniqueId}, URL: ${ad.pop}`);
    try {
      onPopInit();
      const popUrl = ad.pop;
      if (!popUrl) {
        throw new Error('Pop URL is undefined');
      }
      await axios.get(popUrl, axiosConfig);
      console.log(`[CLIENT] POP successful for ad ${ad.uniqueId}`);
      onPopSuccess();
    } catch (error) {
      console.error(`[CLIENT] POP failed for ad ${ad.uniqueId}:`, error);
      onPopError();
    } finally {
      onPopFinally();
    }
  };

  const popAds = async () => {
    if (!queues.current || !queues.current.serverQueue.length) return;

    if (!queues.current.poppedAdsQueue) {
      queues.current.poppedAdsQueue = [];
    }

    while (queues.current.serverQueue.length > 0) {
      const ad = queues.current?.serverQueue[0];
      onUpdateQueue(ad, 'serverQueue');

      if (!ad?.pop) {
        queues.current?.poppedAdsQueue.push(ad);
        return;
      }

      const onPopSuccess = () => {
        queues.current?.poppedAdsQueue.push(ad);
      };

      await triggerPopFlow({ ad, onPopSuccess });
    }
  };

  const getNext = () => {
    if (!queues.current) {
      return;
    }

    popAds();

    const {
      poppedAdsQueue,
    } = queues.current;

    const adArray: Actions[] = [];
    const pushToAdArray = (count: number, action: Actions) => {
      for (let i = 0; i < count; i += 1) {
        adArray.push(action);
      }
    };

    if (poppedAdsQueue?.length > 0) {
      pushToAdArray(config.current.weights?.[AdType.Server], { type: 'SET_AD', payload: poppedAdsQueue[0] });
    }

    // news and weather logic
    if (!componentRef.current?.news.error) {
      pushToAdArray(config.current.weights?.[AdType.News], { type: 'SET_COMPONENT', payload: StrapiComponentType.News });
    }

    if (!componentRef.current?.weather.error) {
      pushToAdArray(config.current.weights?.[AdType.Weather], { type: 'SET_COMPONENT', payload: StrapiComponentType.Weather });
    }

    // fallback logic
    if (adArray.length === 0) {
      const fallbackSize = device?.width && device?.height && `${device?.width}x${device.height}`;

      const fallbackUrl = fallbackSize ? `${cdnURL}/external/fallbacks/${fallbackSize}.png` : '../assets/black.png';
      const fallbackAd: IAdLoop = {
        duration: 6,
        uniqueId: uuidv4(),
        weight: 1,
        createdAt: new Date().getTime(),
        mediaFile: {
          blobURL: fallbackUrl,
          _type: 'image/png',
        },
        pop: undefined,
        type: AdType.Fallback,

      };
      dispatch({ type: 'SET_AD', payload: fallbackAd });
      setTimeout(() => {
        getNext();
      }, 5000);
      return;
    }

    const weightedDispatchIndex = Math.floor(Math.random() * adArray.length);
    dispatch(adArray[weightedDispatchIndex]);
  };

  const getDeviceAd = async () => {
    if ((queues.current?.poppedAdsQueue?.length || 0) >= config.current.queueCap.ads) { return; }

    try {
      const ads = await getDeviceAds({ partnerName: organization });

      if (ads.length === 0 || !queues.current) return;

      ads.forEach((ad) => {
        queues.current?.serverQueue.push(ad);
      });
    } catch (error) {
      console.error('Failed to fetch ads:', error);
    }
  };

  const startProviderLoop = async () => {
    console.log(`[CLIENT] Starting provider loop with interval: ${config.current.clientAdRequestIntervals.ms || 20 * 1000}ms`);
    while (!controllerRef.current.signal.aborted) {
      console.log(`[CLIENT] Making ad request at ${new Date().toISOString()}`);
      await getDeviceAd();
      const delayMs = config.current.clientAdRequestIntervals.ms || 20 * 1000;
      console.log(`[CLIENT] Waiting ${delayMs}ms before next request`);
      await delay(delayMs);
    }
  };

  const fetchInitQueues = async () => {
    dispatch({ type: 'SET_LOADING' });

    // setting up config
    await parseConfig(config);

    dispatch({ type: 'TOGGLE_LOADING', payload: false });
    startProviderLoop();

    handleQueues({
      serverQueue: [],
      weatherQueue: [],
      newsQueue: [],
    });
    getNext();
  };

  const onUpdateQueue = (ad: IAdLoop, queueKey: keyof AdsQueue) => {
    if (queues.current) {
      (queues.current[queueKey] as IAdLoop[]) = cleanCurrentAd(ad, queues.current?.[queueKey] as IAdLoop[]);
    }
  };

  const onAdEnded = () => {
    console.log('ended');
    onUpdateQueue(state.ad, 'poppedAdsQueue');
    getNext();
  };

  const onAdError = () => {
    if (state.error || state.loading || !queues.current) {
      return;
    }

    onUpdateQueue(state.ad, 'poppedAdsQueue');
    getNext();
  };

  useEffect(() => () => {
    controllerRef.current.abort();
  }, []);

  useEffect(() => {
    if (!device) return;
    fetchInitQueues();
    // fetchInitQueues depends on stable refs/functions (config, componentRef, dispatch, handleQueues, getNext)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device]);

  useEffect(() => {
    if (!state.error) {
      return undefined;
    }

    console.log('Resetting ad loop...');
    const timerId = setTimeout(() => {
      fetchInitQueues();
    }, 20 * 1000);

    return () => {
      clearTimeout(timerId);
    };
    // fetchInitQueues is stable, only state.error should trigger this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.error]);

  if (state.loading) {
    return (
      <AdContainer>
        <LoadingSpinner />
      </AdContainer>
    );
  }

  if (state.error) {
    return (
      <AdContainer>
        <BrandedImage
          src={`${cdnURL}/external/fallbacks/432x432.png`}
          alt='Error'
        />
      </AdContainer>
    );
  }

  return (
    <AdContainer>
      <PortlTransition>
        <AdPlayer
          mediaFile={state.ad?.mediaFile}
          duration={state.ad?.duration}
          onEnded={onAdEnded}
          onError={onAdError}
          componentProps={{ componentType: state.component, component: componentRef.current[state.component] }}
        />
      </PortlTransition>
    </AdContainer>
  );
};

export default AdLoop;
